// Smart Sash Admin Panel - RFID Assignment Page
// Unified page: session validation → auth → RFID assignment activation
// Route: /rfid-assignment?sessionId=...&institutionId=...

import { MissingParametersError } from '@/components/auth/MissingParametersError';
import { RegistrationForm } from '@/components/auth/RegistrationForm';
import { SessionExpiredError } from '@/components/auth/SessionExpiredError';
import { SessionInvalidError } from '@/components/auth/SessionInvalidError';
import { Spinner } from '@/components/common';
import {
  AbortedScreen,
  CancelledScreen,
  ClosedScreen,
  ErrorScreen,
  ExpiredScreen,
  LoadingScreen,
  SuccessScreen,
  WaitingScreen,
} from '@/components/rfid-activation';
import { useRFIDAssignment } from '@/hooks/useRFIDAssignment';
import { RFIDAuthProvider } from '@/services/auth';
import React from 'react';

const COPYRIGHT = `© ${new Date().getFullYear()} H. H. Hawkins Ltd.`;

/**
 * Unified RFID Assignment Page
 *
 * Flow:
 *  1. Validate session params from URL query string
 *  2. Show authentication form only when the user does not already have a valid token
 *  3. Subscribe to gRPC and request RFID assignment
 *  4. Display verification code + countdown
 *  5. On success → congratulations screen
 *  6. On cancel/error → revoke request, clear tokens, show terminal screen
 */
export const RFIDAssignment: React.FC = () => {
  const {
    pageStage,
    sessionErrorType,
    sessionErrorMessage,
    sessionId,
    activationStatus,
    verificationCode,
    verificationType,
    timeRemaining,
    activationErrorMessage,
    login,
    handleFormSuccess,
    handleCancel,
    handleStartOver,
    handleGoToLogin,
  } = useRFIDAssignment();

  // ── Session validating ──────────────────────────────────────────────────
  if (pageStage === 'validating') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <Spinner />
          <p className="mt-3 text-muted">Validating session...</p>
        </div>
      </div>
    );
  }

  // ── Session error ───────────────────────────────────────────────────────
  if (pageStage === 'session-error') {
    if (sessionErrorType === 'missing-params') {
      return <MissingParametersError errorMessage={sessionErrorMessage || undefined} />;
    }
    if (sessionErrorType === 'expired') {
      return <SessionExpiredError errorMessage={sessionErrorMessage || undefined} />;
    }
    return <SessionInvalidError errorMessage={sessionErrorMessage || undefined} />;
  }

  // ── Session closed ──────────────────────────────────────────────────────
  if (pageStage === 'closed') {
    return <ClosedScreen onGoToLogin={handleGoToLogin} />;
  }

  // ── Authentication form ─────────────────────────────────────────────────
  if (pageStage === 'form') {
    return (
      <RFIDAuthProvider>
        <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-6 col-lg-5">
                <div className="card shadow-lg border-0">
                  <div className="card-body p-5">
                    <div className="text-center mb-4">
                      <h1 className="h3 mb-3 fw-bold">RFID Assignment</h1>
                      <p className="text-muted">Sign in to continue with RFID assignment</p>
                    </div>
                    <RegistrationForm sessionId={sessionId!} onSuccess={handleFormSuccess} />
                    <div className="text-center mt-4 pt-4 border-top">
                      <p className="text-muted small mb-0">{COPYRIGHT}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </RFIDAuthProvider>
    );
  }

  // ── Activation phase ────────────────────────────────────────────────────
  if (activationStatus === 'loading') return <LoadingScreen />;
  if (activationStatus === 'success')
    return (
      <SuccessScreen
        login={login}
        onGoToLogin={handleGoToLogin}
        title="RFID Assigned"
        message="Your RFID assignment has been completed successfully."
        alertMessage="Your RFID card is now linked and ready to use."
      />
    );
  if (activationStatus === 'expired') return <ExpiredScreen onStartOver={handleStartOver} />;
  if (activationStatus === 'error')
    return <ErrorScreen errorMessage={activationErrorMessage} onStartOver={handleStartOver} />;
  if (activationStatus === 'aborted') return <AbortedScreen onStartOver={handleStartOver} />;
  if (activationStatus === 'cancelled') return <CancelledScreen reason={activationErrorMessage} />;

  // waiting — show verification code
  return (
    <WaitingScreen
      verificationCode={verificationCode}
      verificationType={verificationType}
      timeRemaining={timeRemaining}
      login={login}
      onCancel={handleCancel}
      title="RFID Assignment"
      subtitle="Enter this code to complete RFID assignment"
      hint="Enter this code on your device screen to complete RFID assignment."
    />
  );
};

export default RFIDAssignment;
